import { Helmet } from "react-helmet-async";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Card,
  CardHeader,
  CardContent,
  Stack,
  Container,
  Typography,
  CircularProgress,
  Box,
  Button,
  Grid,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Divider,
  TableContainer,
  TablePagination,
  MenuItem,
} from "@mui/material";
import Iconify from "../components/iconify";
import { get, patch } from "src/utils/networkUtils";
import { fBudgetMillions } from "src/utils/formatNumber";

const PAGE_SIZE = 25;

const TAB_KEYS = {
  OVERVIEW: "overview",
  SQUAD: "squad",
  TEAM_HISTORY: "team-history",
  CAPTAINCY: "captaincy",
  TRANSFERS: "transfers",
  LEAGUES: "leagues",
  BOOSTERS: "boosters",
  SUBSTITUTIONS: "substitutions",
  ACTIVITY: "activity",
};

const TABS = [
  { value: TAB_KEYS.OVERVIEW, label: "Overview" },
  { value: TAB_KEYS.SQUAD, label: "Squad" },
  { value: TAB_KEYS.TEAM_HISTORY, label: "Team History" },
  { value: TAB_KEYS.CAPTAINCY, label: "Captaincy" },
  { value: TAB_KEYS.TRANSFERS, label: "Transfers" },
  { value: TAB_KEYS.LEAGUES, label: "Leagues" },
  { value: TAB_KEYS.BOOSTERS, label: "Boosters" },
  { value: TAB_KEYS.SUBSTITUTIONS, label: "Substitutions" },
  { value: TAB_KEYS.ACTIVITY, label: "Activity" },
];

const TAB_VALUES = TABS.map(tab => tab.value);

function formatDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleString();
}

function formatNumber(value) {
  if (value === null || value === undefined || value === "") return "-";
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return value;
  return new Intl.NumberFormat("en-GB", { maximumFractionDigits: 2 }).format(parsed);
}

function formatPercent(value) {
  if (value === null || value === undefined || value === "") return "-";
  return `${formatNumber(value)}%`;
}

function formatPayload(value) {
  if (!value || (typeof value === "object" && Object.keys(value).length === 0)) return "-";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch (_err) {
    return "-";
  }
}

function Field({ label, value }) {
  return (
    <Grid item xs={12} sm={6} md={3}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" sx={{ wordBreak: "break-word" }}>
        {value ?? "-"}
      </Typography>
    </Grid>
  );
}

function Metric({ label, value }) {
  return (
    <Grid item xs={6} sm={4} md={3}>
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          p: 2,
          minHeight: 88,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h4">{formatNumber(value ?? 0)}</Typography>
      </Box>
    </Grid>
  );
}

function EmptyRow({ colSpan, label }) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} align="center" sx={{ py: 3 }}>
        <Typography color="text.secondary">{label}</Typography>
      </TableCell>
    </TableRow>
  );
}

function TabLoading() {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
      <CircularProgress size={24} />
    </Box>
  );
}

function getErrorMessage(err, fallback = "Failed to load data") {
  return err?.response?.data?.message ?? err?.message ?? fallback;
}

function ResourceError({ error, onRetry }) {
  return (
    <Alert
      severity="error"
      action={
        <Button color="inherit" size="small" onClick={onRetry}>
          Retry
        </Button>
      }
    >
      {error}
    </Alert>
  );
}

function LoadMore({ hasMore, loading, onClick }) {
  if (!hasMore) return null;
  return (
    <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}>
      <Button variant="outlined" onClick={onClick} disabled={loading}>
        {loading ? "Loading..." : "Load more"}
      </Button>
    </Box>
  );
}

function StatusChip({ label }) {
  const normalized = String(label ?? "").toLowerCase();
  const color =
    normalized === "applied" || normalized === "processed" || normalized === "active"
      ? "success"
      : normalized === "pending"
        ? "warning"
        : normalized === "failed" || normalized === "cancelled"
          ? "error"
          : "default";

  return (
    <Chip
      size="small"
      label={label ?? "-"}
      color={color}
      variant={color === "default" ? "outlined" : "filled"}
    />
  );
}

function useLazyArrayResource(userId, active, endpoint) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    get(`/admin/app-users/${userId}/${endpoint}`)
      .then(response => {
        setItems(response?.data ?? []);
        setLoaded(true);
      })
      .catch(err => setError(getErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [endpoint, userId]);

  useEffect(() => {
    setItems([]);
    setLoaded(false);
    setError(null);
  }, [endpoint, userId]);

  // `!error` stops a failed request from being retried in a tight loop; the
  // user retries explicitly via `refresh`.
  useEffect(() => {
    if (active && !loaded && !loading && !error) load();
  }, [active, loaded, loading, error, load]);

  return { items, loading, loaded, error, refresh: load };
}

function usePagedResource(userId, active, endpoint) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(
    (nextOffset = 0) => {
      setLoading(true);
      setError(null);
      get(`/admin/app-users/${userId}/${endpoint}?limit=${PAGE_SIZE}&offset=${nextOffset}`)
        .then(response => {
          const payload = response?.data ?? {};
          const nextItems = payload.items ?? [];
          setItems(prev => (nextOffset === 0 ? nextItems : [...prev, ...nextItems]));
          setTotal(payload.total ?? 0);
          setLoaded(true);
        })
        .catch(err => {
          // A failed "load more" keeps the rows already on screen; the global
          // error dialog reports it. Only a failed first page needs the inline error.
          if (nextOffset === 0) setError(getErrorMessage(err));
        })
        .finally(() => setLoading(false));
    },
    [endpoint, userId]
  );

  useEffect(() => {
    setItems([]);
    setTotal(0);
    setLoaded(false);
    setError(null);
  }, [endpoint, userId]);

  // `!error` stops a failed request from being retried in a tight loop; the
  // user retries explicitly via `refresh`.
  useEffect(() => {
    if (active && !loaded && !loading && !error) load(0);
  }, [active, loaded, loading, error, load]);

  return {
    items,
    total,
    loading,
    loaded,
    error,
    hasMore: items.length < total,
    loadMore: () => load(items.length),
    refresh: () => load(0),
  };
}

function OverviewTab({ user, summary, onEdit }) {
  return (
    <Stack spacing={3}>
      <Card>
        <CardHeader
          title="Profile"
          action={
            <Button
              variant="outlined"
              startIcon={<Iconify icon="eva:edit-outline" />}
              onClick={onEdit}
            >
              Edit
            </Button>
          }
        />
        <CardContent>
          <Grid container spacing={2}>
            <Field label="Username" value={user.username || "(no username)"} />
            <Field label="EngageCraft ID" value={user.engagecraftId} />
            <Field label="Ref ID" value={user.refId} />
            <Field label="Country" value={(user.refCountryCode ?? "").trim() || "-"} />
            <Field label="Fav Club" value={user.refFavClubId} />
            <Field label="Created" value={formatDate(user.createdAt)} />
            <Field label="Last Login" value={formatDate(user.lastLoginAt)} />
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardHeader title="User Summary" />
        <CardContent>
          <Grid container spacing={2}>
            <Metric label="Teams" value={summary?.teamCount} />
            <Metric label="Selected players" value={summary?.selectedPlayerCount} />
            <Metric label="Transfers" value={summary?.transferCount} />
            <Metric label="Pending transfers" value={summary?.pendingTransferCount} />
            <Metric label="Leagues" value={summary?.leagueCount} />
            <Metric label="Boosters" value={summary?.boosterCount} />
            <Metric label="Substitutions" value={summary?.substitutionCount} />
            <Metric label="Activity" value={summary?.activityCount} />
          </Grid>
        </CardContent>
      </Card>
    </Stack>
  );
}

const VIEWABLE_GAMEWEEK_STATUSES = ["locked", "live", "completed", "finalised"];

function SquadTab({ resource, onPlayerOpen }) {
  const [gameweeksByCalendar, setGameweeksByCalendar] = useState({});
  const [loadingGameweeks, setLoadingGameweeks] = useState(false);

  const teams = useMemo(() => resource.items ?? [], [resource.items]);

  useEffect(() => {
    const calendarIds = [...new Set(teams.map(team => team.tournamentCalendarId).filter(Boolean))];
    if (calendarIds.length === 0) {
      setGameweeksByCalendar({});
      return;
    }

    setLoadingGameweeks(true);
    Promise.all(
      calendarIds.map(calendarId =>
        get(`/admin/gameweek?tournamentCalendarId=${calendarId}`)
          .then(response => ({
            calendarId,
            items: response?.data?.gameweeks ?? response?.data ?? [],
          }))
          .catch(() => ({ calendarId, items: [] }))
      )
    )
      .then(results => {
        const map = {};
        results.forEach(({ calendarId, items }) => {
          map[calendarId] = items;
        });
        setGameweeksByCalendar(map);
      })
      .finally(() => setLoadingGameweeks(false));
  }, [teams]);

  const getLatestViewableGameweek = team => {
    const items = gameweeksByCalendar[team.tournamentCalendarId] ?? [];
    const viewable = items.filter(gw => VIEWABLE_GAMEWEEK_STATUSES.includes(gw.status));
    if (viewable.length === 0) return null;
    return viewable.sort((a, b) => (b.number ?? 0) - (a.number ?? 0))[0];
  };

  if (resource.error && !resource.loaded) {
    return <ResourceError error={resource.error} onRetry={resource.refresh} />;
  }
  if (resource.loading && !resource.loaded) return <TabLoading />;

  return (
    <Card>
      <CardHeader title={`Squad (${resource.items.length})`} />
      <CardContent>
        {resource.items.length === 0 && (
          <Typography color="text.secondary">No team created yet.</Typography>
        )}
        {teams.map(team => {
          const latestViewableGameweek = getLatestViewableGameweek(team);

          return (
            <Box key={team.id} sx={{ mb: 4 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap">
                <Typography variant="h6">{team.teamName}</Typography>
                {team.formation && <Chip size="small" label={team.formation} />}
                {team.tournamentName && (
                  <Chip size="small" variant="outlined" label={team.tournamentName} />
                )}
                {team.seasonLabel && (
                  <Chip size="small" variant="outlined" label={team.seasonLabel} />
                )}
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Budget: ${fBudgetMillions(team.budgetRemaining)}`}
                />
                <Chip size="small" variant="outlined" label={`Transfers: ${team.transfersUsed}`} />
                <Chip size="small" variant="outlined" label={`Subs: ${team.substitutionsUsed}`} />
                {team.pendingTransferCount > 0 && (
                  <Chip
                    size="small"
                    color="warning"
                    label={`Pending: ${team.pendingTransferCount}`}
                  />
                )}
                {latestViewableGameweek && (
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={loadingGameweeks}
                    startIcon={<Iconify icon="eva:external-link-outline" />}
                    onClick={() =>
                      window.open(
                        `${process.env.REACT_APP_FRONTEND_URL || ""}/my-team?teamId=${team.id}&gameweekId=${latestViewableGameweek.id}&leaderboardType=overall&pageType=readonly`,
                        "_blank"
                      )
                    }
                  >
                    View on frontend
                  </Button>
                )}
              </Stack>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Slot</TableCell>
                      <TableCell>Player</TableCell>
                      <TableCell>Position</TableCell>
                      <TableCell>Club</TableCell>
                      <TableCell>Price</TableCell>
                      <TableCell>Owned</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Starter</TableCell>
                      <TableCell>Role</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...(team.players ?? [])]
                      .sort((a, b) => (a.positionSlot ?? 99) - (b.positionSlot ?? 99))
                      .map(player => (
                        <TableRow
                          key={player.fantasyPlayerId}
                          hover
                          onClick={() => onPlayerOpen(player.fantasyPlayerId)}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell>{player.positionSlot ?? "-"}</TableCell>
                          <TableCell>{player.name ?? player.fantasyPlayerId}</TableCell>
                          <TableCell>{player.position ?? "-"}</TableCell>
                          <TableCell>{player.clubShortName || player.clubName || "-"}</TableCell>
                          <TableCell>{formatNumber(player.price)}</TableCell>
                          <TableCell>{formatPercent(player.ownershipPct)}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={
                                player.availabilityDisplay ||
                                (player.isVisible === false ? "Hidden" : "Available")
                              }
                              color={player.isVisible === false ? "default" : "success"}
                              variant={player.isVisible === false ? "outlined" : "filled"}
                            />
                          </TableCell>
                          <TableCell>{player.isStarter ? "Yes" : "No"}</TableCell>
                          <TableCell>
                            {player.isCaptain && (
                              <Chip size="small" color="primary" label="C" sx={{ mr: 0.5 }} />
                            )}
                            {player.isViceCaptain && <Chip size="small" label="VC" />}
                          </TableCell>
                        </TableRow>
                      ))}
                    {(team.players ?? []).length === 0 && (
                      <EmptyRow colSpan={9} label="No players." />
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          );
        })}
      </CardContent>
    </Card>
  );
}

function TransfersTab({ resource }) {
  return (
    <Card>
      <CardHeader title={`Transfers (${resource.total})`} />
      <CardContent>
        {resource.error && !resource.loaded ? (
          <ResourceError error={resource.error} onRetry={resource.refresh} />
        ) : resource.loading && !resource.loaded ? (
          <TabLoading />
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>GW</TableCell>
                    <TableCell>Team</TableCell>
                    <TableCell>Out</TableCell>
                    <TableCell>In</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Matchday</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Applied</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resource.items.map(transfer => (
                    <TableRow key={transfer.id}>
                      <TableCell>{transfer.gameweek}</TableCell>
                      <TableCell>{transfer.teamName}</TableCell>
                      <TableCell>{transfer.transferOut ?? "-"}</TableCell>
                      <TableCell>{transfer.transferIn ?? "-"}</TableCell>
                      <TableCell>
                        <StatusChip label={transfer.status} />
                      </TableCell>
                      <TableCell>
                        {transfer.targetMatchdayName || transfer.matchdayName || "-"}
                      </TableCell>
                      <TableCell>{formatDate(transfer.createdAt)}</TableCell>
                      <TableCell>{formatDate(transfer.appliedAt)}</TableCell>
                    </TableRow>
                  ))}
                  {resource.items.length === 0 && <EmptyRow colSpan={8} label="No transfers." />}
                </TableBody>
              </Table>
            </TableContainer>
            <LoadMore
              hasMore={resource.hasMore}
              loading={resource.loading}
              onClick={resource.loadMore}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}

function LeaguesTab({ resource }) {
  return (
    <Card>
      <CardHeader title={`Leagues (${resource.total})`} />
      <CardContent>
        {resource.error && !resource.loaded ? (
          <ResourceError error={resource.error} onRetry={resource.refresh} />
        ) : resource.loading && !resource.loaded ? (
          <TabLoading />
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>League</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Team</TableCell>
                    <TableCell>Rank</TableCell>
                    <TableCell>Points</TableCell>
                    <TableCell>Gameweek</TableCell>
                    <TableCell>Joined</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resource.items.map(league => (
                    <TableRow key={league.id}>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="body2">{league.leagueName}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {league.joinCode}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          <Chip size="small" label={league.leagueType} />
                          {league.isPublic && (
                            <Chip size="small" variant="outlined" label="Public" />
                          )}
                          {league.isCelebrity && (
                            <Chip size="small" variant="outlined" label="Celebrity" />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>{league.teamName}</TableCell>
                      <TableCell>{league.rank ?? "-"}</TableCell>
                      <TableCell>{formatNumber(league.points)}</TableCell>
                      <TableCell>{league.gameWeek ?? "-"}</TableCell>
                      <TableCell>{formatDate(league.joinedAt)}</TableCell>
                    </TableRow>
                  ))}
                  {resource.items.length === 0 && <EmptyRow colSpan={7} label="No leagues." />}
                </TableBody>
              </Table>
            </TableContainer>
            <LoadMore
              hasMore={resource.hasMore}
              loading={resource.loading}
              onClick={resource.loadMore}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}

function BoostersTab({ resource }) {
  return (
    <Card>
      <CardHeader title={`Boosters (${resource.total})`} />
      <CardContent>
        {resource.error && !resource.loaded ? (
          <ResourceError error={resource.error} onRetry={resource.refresh} />
        ) : resource.loading && !resource.loaded ? (
          <TabLoading />
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Booster</TableCell>
                    <TableCell>Team</TableCell>
                    <TableCell>Gameweek</TableCell>
                    <TableCell>12th Man</TableCell>
                    <TableCell>Max Captain</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Activated</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resource.items.map(booster => (
                    <TableRow key={booster.id}>
                      <TableCell>{booster.boosterName}</TableCell>
                      <TableCell>{booster.teamName}</TableCell>
                      <TableCell>
                        {booster.gameweekName} ({booster.gameweekNumber})
                      </TableCell>
                      <TableCell>{booster.player12thMan ?? "-"}</TableCell>
                      <TableCell>
                        {booster.maxCaptainPlayer ?? "-"}
                        {booster.maxCaptainPoints != null
                          ? ` (${formatNumber(booster.maxCaptainPoints)} pts)`
                          : ""}
                      </TableCell>
                      <TableCell>
                        <StatusChip
                          label={
                            booster.isCancelled
                              ? "cancelled"
                              : booster.processed
                                ? "processed"
                                : "active"
                          }
                        />
                      </TableCell>
                      <TableCell>{formatDate(booster.activatedAt)}</TableCell>
                    </TableRow>
                  ))}
                  {resource.items.length === 0 && <EmptyRow colSpan={7} label="No boosters." />}
                </TableBody>
              </Table>
            </TableContainer>
            <LoadMore
              hasMore={resource.hasMore}
              loading={resource.loading}
              onClick={resource.loadMore}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}

function SubstitutionsTab({ resource }) {
  return (
    <Card>
      <CardHeader title={`Substitutions (${resource.total})`} />
      <CardContent>
        {resource.error && !resource.loaded ? (
          <ResourceError error={resource.error} onRetry={resource.refresh} />
        ) : resource.loading && !resource.loaded ? (
          <TabLoading />
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Matchday</TableCell>
                    <TableCell>Team</TableCell>
                    <TableCell>Out</TableCell>
                    <TableCell>In</TableCell>
                    <TableCell>Vice captain</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Applied</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resource.items.map(substitution => (
                    <TableRow key={substitution.id}>
                      <TableCell>
                        {substitution.matchdayName} ({substitution.matchdayNumber})
                      </TableCell>
                      <TableCell>{substitution.teamName}</TableCell>
                      <TableCell>{substitution.subOut ?? "-"}</TableCell>
                      <TableCell>{substitution.subIn ?? "-"}</TableCell>
                      <TableCell>
                        {substitution.viceCaptainDisabled ? "Disabled" : "Unchanged"}
                      </TableCell>
                      <TableCell>
                        <StatusChip label={substitution.isReverted ? "reverted" : "applied"} />
                      </TableCell>
                      <TableCell>{formatDate(substitution.appliedAt)}</TableCell>
                    </TableRow>
                  ))}
                  {resource.items.length === 0 && (
                    <EmptyRow colSpan={7} label="No substitutions." />
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <LoadMore
              hasMore={resource.hasMore}
              loading={resource.loading}
              onClick={resource.loadMore}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}

function ActivityTab({ resource }) {
  return (
    <Card>
      <CardHeader title={`Activity (${resource.total})`} />
      <CardContent>
        {resource.error && !resource.loaded ? (
          <ResourceError error={resource.error} onRetry={resource.refresh} />
        ) : resource.loading && !resource.loaded ? (
          <TabLoading />
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Payload</TableCell>
                    <TableCell>IP</TableCell>
                    <TableCell>User Agent</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resource.items.map(activity => (
                    <TableRow key={activity.id}>
                      <TableCell>
                        <Chip size="small" label={activity.type} />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 320, wordBreak: "break-word" }}>
                        {formatPayload(activity.payload)}
                      </TableCell>
                      <TableCell>{activity.ipAddress ?? "-"}</TableCell>
                      <TableCell sx={{ maxWidth: 260, wordBreak: "break-word" }}>
                        {activity.userAgent ?? "-"}
                      </TableCell>
                      <TableCell>{formatDate(activity.createdAt)}</TableCell>
                    </TableRow>
                  ))}
                  {resource.items.length === 0 && <EmptyRow colSpan={5} label="No activity." />}
                </TableBody>
              </Table>
            </TableContainer>
            <LoadMore
              hasMore={resource.hasMore}
              loading={resource.loading}
              onClick={resource.loadMore}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}

function TeamHistoryTab({ teamsResource, userId }) {
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [gameweeks, setGameweeks] = useState([]);
  const [selectedGameweekId, setSelectedGameweekId] = useState("");
  const [history, setHistory] = useState(null);
  const [loadingGameweeks, setLoadingGameweeks] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const teams = useMemo(() => teamsResource.items ?? [], [teamsResource.items]);

  useEffect(() => {
    if (teams.length > 0 && !selectedTeamId) {
      setSelectedTeamId(teams[0].id);
    }
  }, [teams, selectedTeamId]);

  useEffect(() => {
    if (!selectedTeamId) {
      setGameweeks([]);
      setSelectedGameweekId("");
      return;
    }

    const team = teams.find(t => t.id === selectedTeamId);
    if (!team?.tournamentCalendarId) {
      setGameweeks([]);
      setSelectedGameweekId("");
      return;
    }

    setLoadingGameweeks(true);
    get(`/admin/gameweek?tournamentCalendarId=${team.tournamentCalendarId}`)
      .then(response => {
        const rawItems = response?.data?.gameweeks ?? response?.data ?? [];

        const upcoming = rawItems.filter(gw => gw.status === "upcoming");
        const nonUpcoming = rawItems.filter(gw => gw.status !== "upcoming");
        const sortedUpcoming = upcoming.sort((a, b) => {
          const aStart = a.startsAt ? new Date(a.startsAt).getTime() : 0;
          const bStart = b.startsAt ? new Date(b.startsAt).getTime() : 0;
          if (aStart !== bStart) return aStart - bStart;
          return (a.number ?? 0) - (b.number ?? 0);
        });
        const filteredItems = nonUpcoming.length > 0 ? nonUpcoming : sortedUpcoming.slice(0, 1);

        const active = filteredItems.filter(
          gw => gw.status === "live" || gw.status === "open" || gw.status === "locked"
        );
        const completed = filteredItems.filter(gw => gw.status === "finalised");
        const defaultGw =
          active[0] ??
          (completed.length > 0
            ? completed.sort((a, b) => (b.number ?? 0) - (a.number ?? 0))[0]
            : null) ??
          sortedUpcoming[0] ??
          null;

        setGameweeks(filteredItems);
        setSelectedGameweekId(defaultGw?.id ?? "");
      })
      .finally(() => setLoadingGameweeks(false));
  }, [selectedTeamId, teams]);

  useEffect(() => {
    if (!selectedTeamId || !selectedGameweekId) {
      setHistory(null);
      return;
    }

    setLoadingHistory(true);
    get(`/admin/app-users/${userId}/teams/${selectedTeamId}/gameweeks/${selectedGameweekId}`)
      .then(response => {
        setHistory(response?.data ?? null);
      })
      .finally(() => setLoadingHistory(false));
  }, [selectedTeamId, selectedGameweekId, userId]);

  const sortedPlayers = [...(history?.players ?? [])].sort(
    (a, b) => (a.positionSlot ?? 99) - (b.positionSlot ?? 99)
  );

  const startingXIValue =
    history?.players
      ?.filter(
        player => player.isStarter && player.fantasyPlayerId !== history.twelfthMan?.fantasyPlayerId
      )
      ?.reduce((sum, player) => sum + (player.player?.price ?? 0), 0) ?? 0;

  return (
    <Card>
      <CardHeader title="Team History" />
      <CardContent>
        <Stack direction="row" spacing={2} sx={{ mb: 3 }} flexWrap="wrap">
          <TextField
            select
            label="Team"
            value={selectedTeamId}
            onChange={e => setSelectedTeamId(e.target.value)}
            disabled={teamsResource.loading || teams.length === 0}
            sx={{ minWidth: 240 }}
            size="small"
          >
            {teams.map(team => (
              <MenuItem key={team.id} value={team.id}>
                {team.teamName}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Gameweek"
            value={selectedGameweekId}
            onChange={e => setSelectedGameweekId(e.target.value)}
            disabled={loadingGameweeks || gameweeks.length === 0}
            sx={{ minWidth: 200 }}
            size="small"
          >
            {gameweeks.map(gw => (
              <MenuItem key={gw.id} value={gw.id}>
                {gw.name ?? `GW ${gw.number}`} ({gw.status})
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        {teamsResource.error && !teamsResource.loaded ? (
          <ResourceError error={teamsResource.error} onRetry={teamsResource.refresh} />
        ) : loadingHistory || teamsResource.loading ? (
          <TabLoading />
        ) : history ? (
          <Stack spacing={3}>
            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
              {history.formation && <Chip size="small" label={history.formation} />}
              <Chip
                size="small"
                variant="outlined"
                label={`GW Points: ${formatNumber(history.gameweekPoints ?? history.totalPoints)}`}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`Total: ${formatNumber(history.totalPoints)}`}
              />
              {history.rank != null && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Rank: ${formatNumber(history.rank)}`}
                />
              )}
              {history.activeBoosterCode && (
                <Chip size="small" color="info" label={`Booster: ${history.activeBoosterCode}`} />
              )}
              <Chip
                size="small"
                variant="outlined"
                label={`Squad Value: ${fBudgetMillions(startingXIValue)}`}
              />
            </Stack>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Slot</TableCell>
                    <TableCell>Player</TableCell>
                    <TableCell>Position</TableCell>
                    <TableCell>Club</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell>Points</TableCell>
                    <TableCell>Role</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedPlayers.map(player => (
                    <TableRow key={player.id ?? player.fantasyPlayerId}>
                      <TableCell>{player.positionSlot ?? "-"}</TableCell>
                      <TableCell>
                        {player.player?.displayName ??
                          player.player?.name ??
                          player.fantasyPlayerId}
                      </TableCell>
                      <TableCell>{player.player?.position ?? "-"}</TableCell>
                      <TableCell>
                        {player.player?.contestantShortName ?? player.player?.contestantName ?? "-"}
                      </TableCell>
                      <TableCell>
                        {fBudgetMillions(player.gameweekPrice ?? player.player?.price)}
                      </TableCell>
                      <TableCell>
                        {formatNumber(player.gameweekPoints ?? player.totalPoints ?? 0)}
                      </TableCell>
                      <TableCell>
                        {player.isCaptain && (
                          <Chip size="small" color="primary" label="C" sx={{ mr: 0.5 }} />
                        )}
                        {player.isViceCaptain && <Chip size="small" label="VC" />}
                        {history.effectiveCaptainFantasyPlayerId === player.fantasyPlayerId &&
                          !player.isCaptain && (
                            <Chip size="small" color="success" label="Effective C" />
                          )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {history.twelfthManPlayer && (
                    <TableRow key={`${history.twelfthManPlayer.fantasyPlayerId}-12th`}>
                      <TableCell>-</TableCell>
                      <TableCell>
                        {history.twelfthManPlayer.player?.displayName ??
                          history.twelfthManPlayer.fantasyPlayerId}
                      </TableCell>
                      <TableCell>{history.twelfthManPlayer.player?.position ?? "-"}</TableCell>
                      <TableCell>
                        {history.twelfthManPlayer.player?.contestantShortName ??
                          history.twelfthManPlayer.player?.contestantName ??
                          "-"}
                      </TableCell>
                      <TableCell>
                        {fBudgetMillions(history.twelfthManPlayer.gameweekPrice)}
                      </TableCell>
                      <TableCell>
                        {formatNumber(history.twelfthManPlayer.gameweekPoints ?? 0)}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" color="secondary" label="12th Man" />
                      </TableCell>
                    </TableRow>
                  )}
                  {sortedPlayers.length === 0 && !history.twelfthManPlayer && (
                    <EmptyRow colSpan={7} label="No players." />
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        ) : (
          <Typography color="text.secondary">
            Select a team and gameweek to view historical setup.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function CaptaincyTab({ teamsResource, userId }) {
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [history, setHistory] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);

  const teams = useMemo(() => teamsResource.items ?? [], [teamsResource.items]);
  const PAGE_SIZE = 25;

  useEffect(() => {
    if (teams.length > 0 && !selectedTeamId) {
      setSelectedTeamId(teams[0].id);
    }
  }, [teams, selectedTeamId]);

  useEffect(() => {
    if (!selectedTeamId) {
      setHistory({ items: [], total: 0 });
      return;
    }

    setLoading(true);
    get(
      `/admin/app-users/${userId}/teams/${selectedTeamId}/captaincy-history?limit=${PAGE_SIZE}&offset=${
        page * PAGE_SIZE
      }`
    )
      .then(response => {
        setHistory(response?.data ?? { items: [], total: 0 });
      })
      .finally(() => setLoading(false));
  }, [selectedTeamId, userId, page]);

  return (
    <Card>
      <CardHeader title="Captaincy History" />
      <CardContent>
        <Stack direction="row" spacing={2} sx={{ mb: 3 }} flexWrap="wrap">
          <TextField
            select
            label="Team"
            value={selectedTeamId}
            onChange={e => {
              setSelectedTeamId(e.target.value);
              setPage(0);
            }}
            disabled={teamsResource.loading || teams.length === 0}
            sx={{ minWidth: 240 }}
            size="small"
          >
            {teams.map(team => (
              <MenuItem key={team.id} value={team.id}>
                {team.teamName}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        {teamsResource.error && !teamsResource.loaded ? (
          <ResourceError error={teamsResource.error} onRetry={teamsResource.refresh} />
        ) : loading || teamsResource.loading ? (
          <TabLoading />
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Player</TableCell>
                    <TableCell>From</TableCell>
                    <TableCell>To</TableCell>
                    <TableCell>Gameweek</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Applied</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.items.map(row => (
                    <TableRow key={row.id}>
                      <TableCell>{formatDate(row.createdAt)}</TableCell>
                      <TableCell>{row.playerName ?? row.fantasyPlayerId}</TableCell>
                      <TableCell>{row.fromRole}</TableCell>
                      <TableCell>{row.toRole}</TableCell>
                      <TableCell>{row.gameweekName ?? row.gameweek ?? "-"}</TableCell>
                      <TableCell>{row.source}</TableCell>
                      <TableCell>
                        <StatusChip label={row.status} />
                      </TableCell>
                      <TableCell>{formatDate(row.appliedAt)}</TableCell>
                    </TableRow>
                  ))}
                  {history.items.length === 0 && (
                    <EmptyRow colSpan={8} label="No captaincy history." />
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={history.total}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function UserDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");

  const [user, setUser] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(
    TAB_VALUES.includes(tabParam) ? tabParam : TAB_KEYS.OVERVIEW
  );

  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({
    username: "",
    refCountryCode: "",
    refFavClubId: "",
  });
  const [saving, setSaving] = useState(false);

  const teams = useLazyArrayResource(
    userId,
    activeTab === TAB_KEYS.SQUAD ||
      activeTab === TAB_KEYS.TEAM_HISTORY ||
      activeTab === TAB_KEYS.CAPTAINCY,
    "teams"
  );
  const transfers = usePagedResource(userId, activeTab === TAB_KEYS.TRANSFERS, "transfers");
  const leagues = usePagedResource(userId, activeTab === TAB_KEYS.LEAGUES, "leagues");
  const boosters = usePagedResource(userId, activeTab === TAB_KEYS.BOOSTERS, "boosters");
  const substitutions = usePagedResource(
    userId,
    activeTab === TAB_KEYS.SUBSTITUTIONS,
    "substitutions"
  );
  const activity = usePagedResource(userId, activeTab === TAB_KEYS.ACTIVITY, "activity");

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([get(`/admin/app-users/${userId}`), get(`/admin/app-users/${userId}/summary`)])
      .then(([userResponse, summaryResponse]) => {
        setUser(userResponse?.data ?? null);
        setSummary(summaryResponse?.data ?? null);
      })
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (TAB_VALUES.includes(tabParam) && tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
  }, [activeTab, tabParam]);

  const changeTab = (_event, value) => {
    setActiveTab(value);
    const nextParams = new URLSearchParams(searchParams);
    if (value === TAB_KEYS.OVERVIEW) {
      nextParams.delete("tab");
    } else {
      nextParams.set("tab", value);
    }
    setSearchParams(nextParams, { replace: true });
  };

  const openEdit = () => {
    setForm({
      username: user?.username ?? "",
      refCountryCode: user?.refCountryCode?.trim() ?? "",
      refFavClubId: user?.refFavClubId ?? "",
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const res = await patch(`/admin/app-users/${userId}`, {
        username: form.username || null,
        refCountryCode: form.refCountryCode || null,
        refFavClubId: form.refFavClubId || null,
      });
      if (res?.data) setUser(res.data);
      setEditOpen(false);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Container>
        <Button
          startIcon={<Iconify icon="eva:arrow-back-outline" />}
          onClick={() => navigate("/dashboard/users")}
        >
          Back to Users
        </Button>
        <Typography variant="h5" sx={{ mt: 3 }}>
          User not found.
        </Typography>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>User Detail | DreamTeam Admin</title>
      </Helmet>

      <Container maxWidth="xl">
        <Button
          startIcon={<Iconify icon="eva:arrow-back-outline" />}
          onClick={() => navigate("/dashboard/users")}
          sx={{ mb: 2 }}
        >
          Back to Users
        </Button>

        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ md: "center" }}
          justifyContent="space-between"
          mb={3}
        >
          <Box>
            <Typography variant="h4">{user.username || "(no username)"}</Typography>
            <Typography variant="body2" color="text.secondary">
              {user.refId} - EngageCraft {user.engagecraftId}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {(user.refCountryCode ?? "").trim() && (
              <Chip label={(user.refCountryCode ?? "").trim()} />
            )}
            {user.refFavClubId && <Chip variant="outlined" label={user.refFavClubId} />}
          </Stack>
        </Stack>

        <Card sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={changeTab}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ px: 2 }}
          >
            {TABS.map(tab => (
              <Tab key={tab.value} value={tab.value} label={tab.label} />
            ))}
          </Tabs>
          <Divider />
        </Card>

        {activeTab === TAB_KEYS.OVERVIEW && (
          <OverviewTab user={user} summary={summary} onEdit={openEdit} />
        )}
        {activeTab === TAB_KEYS.SQUAD && (
          <SquadTab resource={teams} onPlayerOpen={id => navigate(`/dashboard/players/${id}`)} />
        )}
        {activeTab === TAB_KEYS.TEAM_HISTORY && (
          <TeamHistoryTab teamsResource={teams} userId={userId} />
        )}
        {activeTab === TAB_KEYS.CAPTAINCY && <CaptaincyTab teamsResource={teams} userId={userId} />}
        {activeTab === TAB_KEYS.TRANSFERS && <TransfersTab resource={transfers} />}
        {activeTab === TAB_KEYS.LEAGUES && <LeaguesTab resource={leagues} />}
        {activeTab === TAB_KEYS.BOOSTERS && <BoostersTab resource={boosters} />}
        {activeTab === TAB_KEYS.SUBSTITUTIONS && <SubstitutionsTab resource={substitutions} />}
        {activeTab === TAB_KEYS.ACTIVITY && <ActivityTab resource={activity} />}
      </Container>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Username"
              value={form.username}
              onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Country code"
              value={form.refCountryCode}
              onChange={e => setForm(f => ({ ...f, refCountryCode: e.target.value }))}
              inputProps={{ maxLength: 3 }}
              fullWidth
            />
            <TextField
              label="Favourite club"
              value={form.refFavClubId}
              onChange={e => setForm(f => ({ ...f, refFavClubId: e.target.value }))}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveEdit} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
